import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { EyeOutlined, EyeInvisibleOutlined } from "@ant-design/icons";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import { message } from "antd";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";

const Container = styled.div`
  min-height: 100vh;
  background: #fff;
  display: flex;
  flex-direction: column;
`;

const ContentWrapper = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;

  @media (max-width: 768px) {
    padding: 20px;
  }
`;

const LoginBox = styled.div`
  background: #fff;
  border: 1px solid #eee;
  border-radius: 8px;
  padding: 50px;
  width: 100%;
  max-width: 420px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);

  @media (max-width: 768px) {
    padding: 30px 20px;
    max-width: 100%;
    border: none;
    box-shadow: none;
  }
`;

const LoginHeading = styled.h1`
  font-size: 32px;
  font-weight: 700;
  color: #000;
  margin: 0 0 12px 0;
  text-align: center;

  @media (max-width: 768px) {
    font-size: 24px;
  }
`;

const LoginSubtitle = styled.p`
  font-size: 14px;
  color: #666;
  text-align: center;
  margin: 0 0 40px 0;

  @media (max-width: 768px) {
    margin-bottom: 30px;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 24px;

  @media (max-width: 768px) {
    margin-bottom: 20px;
  }
`;

const Label = styled.label`
  font-size: 13px;
  font-weight: 600;
  color: #000;
  display: block;
`;

const InputWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 14px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  font-family: inherit;
  transition: all 0.3s;
  padding-right: ${(props) => (props.hasIcon ? "40px" : "14px")};

  &:focus {
    outline: none;
    border-color: #000;
    box-shadow: 0 0 0 3px rgba(0, 0, 0, 0.05);
  }

  &::placeholder {
    color: #999;
  }

  @media (max-width: 768px) {
    padding: 11px 12px;
    font-size: 13px;
  }
`;

const PasswordToggle = styled.button`
  position: absolute;
  right: 12px;
  background: none;
  border: none;
  cursor: pointer;
  color: #999;
  font-size: 16px;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s;

  &:hover {
    color: #666;
  }
`;

const InputHint = styled.span`
  font-size: 12px;
  color: #999;
  margin-top: 4px;
`;

const ErrorMessage = styled.span`
  font-size: 12px;
  color: #ff4444;
  margin-top: 4px;
`;

const RememberSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 28px;

  @media (max-width: 768px) {
    margin-bottom: 24px;
  }
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font-size: 13px;
  color: #666;
  user-select: none;
`;

const Checkbox = styled.input`
  width: 16px;
  height: 16px;
  cursor: pointer;
  accent-color: #000;
`;

const ForgotPassword = styled.a`
  font-size: 13px;
  color: #000;
  text-decoration: none;
  font-weight: 600;
  transition: all 0.3s;

  &:hover {
    color: #666;
  }
`;

const LoginButton = styled.button`
  width: 100%;
  padding: 14px 20px;
  background: #000;
  color: #fff;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s;
  margin-bottom: 20px;

  &:hover {
    background: #333;
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    background: #ccc;
    cursor: not-allowed;
    transform: none;
  }

  @media (max-width: 768px) {
    padding: 12px 16px;
    font-size: 13px;
    margin-bottom: 18px;
  }
`;

const Divider = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
  color: #999;
  font-size: 13px;

  &::before,
  &::after {
    content: "";
    flex: 1;
    height: 1px;
    background: #eee;
  }
`;

const SocialLogin = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-bottom: 24px;

  @media (max-width: 768px) {
    gap: 10px;
    margin-bottom: 20px;
  }
`;

const SocialButton = styled.button`
  padding: 12px 16px;
  background: #f9f9f9;
  border: 1px solid #eee;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  &:hover {
    background: #f0f0f0;
    border-color: #ddd;
  }

  @media (max-width: 768px) {
    padding: 10px 12px;
    font-size: 12px;
  }
`;

const SignupSection = styled.div`
  text-align: center;
  padding-top: 20px;
  border-top: 1px solid #eee;
`;

const SignupText = styled.p`
  font-size: 13px;
  color: #666;
  margin: 0 0 8px 0;
`;

const SignupLink = styled.a`
  font-size: 13px;
  color: #000;
  text-decoration: none;
  font-weight: 700;
  transition: all 0.3s;
  cursor: pointer;
  &:hover {
    color: #666;
  }
`;

const SuccessMessage = styled.div`
  background: #f0f0f0;
  border-left: 4px solid #000;
  padding: 12px 14px;
  border-radius: 4px;
  margin-bottom: 20px;
  font-size: 13px;
  color: #000;
  font-weight: 500;

  @media (max-width: 768px) {
    margin-bottom: 16px;
  }
`;

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errors, setErrors] = useState({});
  const [inputType, setInputType] = useState("empty"); // 'empty', 'email', 'phone'
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });
  const product = location.state?.product;
  // Detect if input is phone or email
  const detectInputType = (value) => {
    const trimmedValue = value.trim();
    if (!trimmedValue) {
      return "empty";
    }
    // Check if it's a phone number (only digits)
    if (/^\d+$/.test(trimmedValue)) {
      return "phone";
    }
    // Check if it contains @ (likely email)
    if (trimmedValue.includes("@")) {
      return "email";
    }
    // If it has numbers but no @, assume phone (still being typed)
    if (/^\d/.test(trimmedValue)) {
      return "phone";
    }
    // Default to email if it has letters and no @
    return "email";
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Detect input type on every change
    if (name === "email") {
      setInputType(detectInputType(value));
    }

    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    const value = formData.email.trim();

    if (!value) {
      newErrors.email = "Email or phone number is required";
    } else if (inputType === "phone") {
      // Phone validation
      const phoneDigits = value.replace(/\D/g, "");
      if (phoneDigits.length !== 10) {
        newErrors.email = "Phone number must be exactly 10 digits";
      }
    } else if (inputType === "email") {
      // Email validation
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
        newErrors.email = "Please enter a valid email address";
      }
    } else {
      newErrors.email =
        "Please enter a valid email address or 10-digit phone number";
    }

    // Password validation
    if (!formData.password) {
      newErrors.password = "Password is required";
    } else if (formData.password.length < 6) {
      newErrors.password = "Password must be at least 6 characters";
    }

    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const newErrors = validateForm();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setLoading(true);
    setErrors({});

    try {
      const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
      const response = await fetch(`${apiBaseUrl}/auth/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          phone: formData.email,
          password: formData.password,
        }),
      });

      const data = await response.json();

      if (data.status && data.data) {
        // Login successful
        const { user, token } = data.data;

        // Dispatch to Redux store
        dispatch({
          type: "logIn",
          data: {
            token: token,
            userId: user.id,
            user: user,
          },
        });

        // Show success message
        message.success(data.message || "Login successful!");
        setSuccessMessage("Login successful! Redirecting...");
        setFormData({ email: "", password: "" });
        if (product) {
          navigate(`/product/${product.slug}`, { state: { product } });
        } else {
          navigate("/");
        }
      } else {
        // Login failed
        message.error(data.message || "Login failed. Please try again.");
        setErrors({
          submit: data.message || "Login failed. Please try again.",
        });
      }
    } catch (error) {
      console.error("Login error:", error);
      const errorMessage = error.message || "Login failed. Please try again.";
      message.error(errorMessage);
      setErrors({
        submit: errorMessage,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSocialLogin = (provider) => {
    // Handle social login
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <Container>
      {/* <Navbar /> */}

      <ContentWrapper>
        <LoginBox>
          <LoginHeading>Welcome Back</LoginHeading>
          <LoginSubtitle>Sign in to your account to continue</LoginSubtitle>

          {successMessage && <SuccessMessage>{successMessage}</SuccessMessage>}

          <form onSubmit={handleSubmit}>
            {/* Email/Phone Field */}
            <FormGroup>
              <Label htmlFor="email">Phone Number</Label>
              <Input
                type="text"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="Enter your phone number"
                disabled={loading}
              />
              {/* {inputType === "phone" && (
                <InputHint>10-digit phone number detected</InputHint>
              )}
              {inputType === "email" && (
                <InputHint>Email address detected</InputHint>
              )} */}
              {errors.email && <ErrorMessage>{errors.email}</ErrorMessage>}
            </FormGroup>

            {/* Password Field */}
            <FormGroup>
              <Label htmlFor="password">Password</Label>
              <InputWrapper>
                <Input
                  type={showPassword ? "text" : "password"}
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  placeholder="Enter your password"
                  hasIcon={true}
                  disabled={loading}
                />
                <PasswordToggle
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  tabIndex="-1"
                  disabled={loading}
                >
                  {showPassword ? <EyeInvisibleOutlined /> : <EyeOutlined />}
                </PasswordToggle>
              </InputWrapper>
              {errors.password && (
                <ErrorMessage>{errors.password}</ErrorMessage>
              )}
            </FormGroup>

            {/* Remember Me & Forgot Password */}
            <RememberSection>
              <CheckboxLabel>
                <Checkbox
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  disabled={loading}
                />
                Remember me
              </CheckboxLabel>
              <ForgotPassword href="/forgot-password">
                Forgot password?
              </ForgotPassword>
            </RememberSection>

            {/* Login Button */}
            <LoginButton type="submit" disabled={loading}>
              {loading ? "Signing in..." : "Sign in"}
            </LoginButton>
          </form>

          {/* Sign Up Link */}
          <SignupSection>
            <SignupText>Don't have an account?</SignupText>
            <SignupLink
              onClick={() => {
                navigate("/signup");
              }}
            >
              Create account now
            </SignupLink>
          </SignupSection>
        </LoginBox>
      </ContentWrapper>

      <Footer />
    </Container>
  );
};

export default Login;
